/**
 * Intent concept model for the hub command router.
 *
 * The literal `triggers` in `chatRecommendations.ts` only catch phrasings close
 * to a tool's name. Real users describe a *situation* ("my kid keeps melting
 * down", "I have to tell a mom her son is failing") without ever naming the
 * tool. This module maps everyday language to canonical "concepts" so the
 * matcher can reason about intent instead of exact strings.
 *
 * Two concept tiers:
 *  - PRIMARY: a subject/topic strong enough to justify a suggestion on its own
 *    (e.g. "student", "behavior", "anxiety").
 *  - SUPPORTING: adds confidence but never triggers alone (e.g. "conversation").
 *    "I need to have a conversation" should not, by itself, pick a destination.
 */

export type ConceptName =
  | "student"
  | "parent"
  | "colleague"
  | "leadership"
  | "board"
  | "behavior"
  | "anxiety"
  | "balance"
  | "spark"
  | "coaching"
  | "impact"
  | "decision"
  | "history"
  | "insights"
  | "resources"
  | "team_admin"
  | "invite"
  | "conversation";

type ConceptDefinition = {
  primary: boolean;
  /** Single words (already singularized) matched against the message token set. */
  words: string[];
  /** Multi-word phrases matched as word-boundary substrings. */
  phrases?: string[];
};

export const CONCEPT_DEFS: Record<ConceptName, ConceptDefinition> = {
  student: {
    primary: true,
    words: [
      "student",
      "kid",
      "kiddo",
      "child",
      "children",
      "learner",
      "pupil",
      "son",
      "daughter",
      "teen",
      "teenager",
      "class",
      "classroom",
    ],
    phrases: ["my class", "in class", "this kid", "a kid"],
  },
  parent: {
    primary: true,
    words: [
      "parent",
      "mom",
      "mum",
      "mommy",
      "momma",
      "dad",
      "daddy",
      "mother",
      "father",
      "guardian",
      "family",
      "caregiver",
      "household",
    ],
    phrases: ["call home", "phone home", "email home", "the family"],
  },
  colleague: {
    primary: true,
    words: [
      "colleague",
      "coworker",
      "teammate",
      "peer",
      "faculty",
      "department",
      "aide",
      "para",
      "paraprofessional",
    ],
    phrases: ["co-worker", "co worker", "grade level", "team member", "my team", "a teacher"],
  },
  leadership: {
    primary: true,
    words: ["supervise", "manage", "evaluate", "underperforming", "subordinate", "direct-report"],
    phrases: ["my staff", "lead my team", "leading my team", "manage my", "teacher i supervise", "direct report"],
  },
  board: {
    primary: true,
    words: ["board", "trustee", "governance", "superintendent"],
    phrases: ["school board", "board member", "board meeting"],
  },
  behavior: {
    primary: true,
    words: [
      "behavior",
      "behaviour",
      "incident",
      "meltdown",
      "outburst",
      "tantrum",
      "disruptive",
      "disrupting",
      "defiant",
      "discipline",
      "disciplinary",
      "suspension",
      "suspend",
      "detention",
      "referral",
      "fight",
      "fighting",
      "bullying",
      "expulsion",
    ],
    phrases: [
      "acting out",
      "acted out",
      "acts out",
      "melting down",
      "blew up",
      "code of conduct",
      "sent to the office",
      "kicked out",
      "won't stop",
      "keeps disrupting",
    ],
  },
  anxiety: {
    primary: true,
    words: [
      "anxious",
      "anxiety",
      "panic",
      "panicking",
      "nervous",
      "overwhelmed",
      "overwhelm",
      "stressed",
      "stress",
      "racing",
      "spiraling",
      "spiralling",
      "frazzled",
      "tense",
      "dysregulated",
      "shaky",
      "rattled",
    ],
    phrases: ["freaking out", "freaked out", "can't breathe", "heart racing", "on edge", "losing it"],
  },
  balance: {
    primary: true,
    words: [
      "burnout",
      "burnedout",
      "burntout",
      "exhausted",
      "drained",
      "depleted",
      "overworked",
      "boundaries",
      "boundary",
    ],
    phrases: [
      "burned out",
      "burnt out",
      "work life balance",
      "work-life balance",
      "no energy",
      "running on empty",
      "spread thin",
      "stretched thin",
      "too much on my plate",
      "can't keep up",
      "no time for",
    ],
  },
  spark: {
    primary: true,
    words: ["spark", "passion", "purpose", "joy", "meaning", "inspired", "inspiration", "reignite", "numb"],
    phrases: [
      "why i teach",
      "why i got into",
      "lost my spark",
      "lost my passion",
      "going through the motions",
      "remember why",
      "fell out of love",
    ],
  },
  coaching: {
    primary: true,
    words: ["coach", "coaching", "vent", "advice", "guidance"],
    phrases: ["think out loud", "process this", "process out loud", "sounding board", "talk it through", "need to vent"],
  },
  impact: {
    primary: true,
    words: ["impact", "difference", "leverage", "legacy"],
    phrases: ["make a difference", "bigger impact", "more impact", "scale up", "next chapter", "career growth", "stretch goal"],
  },
  decision: {
    primary: true,
    words: ["decision", "decide", "choice", "choose", "torn", "weighing", "weigh", "dilemma", "stuck", "option", "tradeoff", "strategy", "strategic"],
    phrases: [
      "trade off",
      "trade-off",
      "30 60 90",
      "figure out what to do",
      "think it through",
      "think this through",
      "thinking through",
      "should i",
      "torn between",
      "not sure what to do",
      "weigh the options",
    ],
  },
  history: {
    primary: true,
    words: ["history", "previous", "prior", "earlier", "resume"],
    phrases: ["past chat", "past conversation", "previous conversation", "old conversation", "left off", "pick up where", "earlier chat"],
  },
  insights: {
    primary: true,
    words: ["insight", "pattern", "trend", "progress"],
    phrases: ["my growth", "what patterns", "generated for me", "my trends"],
  },
  resources: {
    primary: true,
    words: ["handout", "library", "worksheet", "material", "pdf"],
    phrases: ["my resources", "saved resources", "saved handouts", "resource library", "my library"],
  },
  team_admin: {
    primary: true,
    words: [],
    phrases: ["my teams", "team dashboard", "my team's", "educators on my team", "coaching my staff", "how are my teachers", "support my staff"],
  },
  invite: {
    primary: true,
    words: ["invite", "invitation", "onboard"],
    phrases: ["add a colleague", "add a teammate", "bring someone", "share hey bodhi", "invite a teammate", "invite teammates", "get my team on"],
  },
  conversation: {
    primary: false,
    words: ["conversation", "talk", "chat", "speak", "convo", "discuss", "discussion", "tell", "email", "call", "phone", "message"],
    phrases: ["reach out", "sit down with", "hard conversation", "difficult conversation", "tough conversation"],
  },
};

/** Canonical concepts each destination cares about. */
export const REC_CONCEPTS: Record<string, ConceptName[]> = {
  behavior_toolkit: ["behavior"],
  strategic_decision: ["decision"],
  reset_my_nervous_system: ["anxiety"],
  reclaim_my_balance: ["balance"],
  expand_my_impact: ["impact"],
  connect_with_students: ["student", "conversation"],
  strengthen_my_voice: ["parent", "conversation"],
  navigate_parent_relationships: ["parent", "conversation"],
  lead_with_clarity: ["colleague", "conversation"],
  leading_your_staff: ["colleague", "leadership", "conversation"],
  navigate_board_relationships: ["board", "conversation"],
  get_coaching: ["coaching"],
  renew_my_spark: ["spark"],
  history: ["history"],
  insights: ["insights"],
  resources: ["resources"],
  my_teams: ["team_admin"],
  invite_teammates: ["invite"],
};

const STOPWORDS = new Set([
  "the", "a", "an", "and", "or", "but", "to", "of", "in", "on", "at", "for", "with", "my", "me",
  "i", "im", "ive", "is", "am", "are", "was", "were", "be", "been", "this", "that", "it", "its",
  "im", "do", "dont", "have", "has", "had", "need", "want", "just", "really", "so", "very", "about",
  "how", "what", "when", "where", "who", "can", "could", "would", "should", "help", "please",
]);

/** Singularize a token (handles common English plurals). */
export function singularize(word: string): string {
  if (word.length <= 3) return word;
  if (word.endsWith("ies")) return `${word.slice(0, -3)}y`;
  if (/(ses|xes|zes|ches|shes)$/.test(word)) return word.slice(0, -2);
  if (word.endsWith("ss")) return word;
  if (word.endsWith("s")) return word.slice(0, -1);
  return word;
}

/** Normalize a message into a set of meaningful, singularized tokens. */
export function tokenizeMessage(message: string): Set<string> {
  const cleaned = message.toLowerCase().replace(/[^a-z0-9\s]+/g, " ");
  const tokens = cleaned.split(/\s+/).filter(Boolean);
  const out = new Set<string>();
  for (const raw of tokens) {
    if (STOPWORDS.has(raw)) continue;
    const singular = singularize(raw);
    out.add(raw);
    out.add(singular);
  }
  return out;
}
